import { useOrchidPro } from "components/Dashboard/DoctorDashboard/contexts/orchid-pro-context";
import ErrorMessage from "components/error-message";
import SuccessMessage from "components/success-message";
import SuccessTag from "components/success-tag/SuccessTag";
import ToggleSwitch from "components/toggle-switch";
import { useTimeOnPageContext } from "contexts/time-on-page";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Spinner, Tab, Tabs } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import postOrchidPro from "services/postOrchidPro";
import { AmplitudePageName, track } from "utils/amplitude";
import styles from "./Notifications.module.scss";

const AMPLITUDE_PAGE_NAME: AmplitudePageName = "pro-settings-notifications";

const NOTIFICATION_SETTING_OPTIONS = [
  { value: "notifications", label: "Notifications" },
];

const NotificationsSetting = () => {
  const { setPage } = useTimeOnPageContext();
  useEffect(() => setPage(AMPLITUDE_PAGE_NAME), [setPage]);

  const copyLinkTarget = useRef(null);
  const [copied, setCopied] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const { activeTab } = useMemo(() => {
    const query = new URLSearchParams(location.search);
    return {
      activeTab: query.get("tab") || "",
    };
  }, [location.search]);

  const [tab, setTab] = useState(activeTab || "notifications");
  const [changed, setChanged] = useState(false);

  const { orchidPro, setOrchidPro } = useOrchidPro();

  useEffect(() => {
    if (orchidPro?.allow_email_notifications === undefined) return;
    setAllow(orchidPro.allow_email_notifications);
  }, [orchidPro]);

  const [allow, setAllow] = useState<boolean>(false);
  const [error, setError] = useState<any>();
  const [success, setSuccess] = useState<string>();

  const [busy, setBusy] = useState(false);

  const saveNotificationsSettings = async () => {
    setBusy(true);
    try {
      setOrchidPro(
        await postOrchidPro({
          allow_email_notifications: allow,
        }),
      );
      setSuccess("Saved");
    } catch (error) {
      setError(error);
    } finally {
      setBusy(false);
    }
  };

  const onAllowEmailChange = () => {
    setAllow((s) => !s);
    setChanged(true);
  };

  return (
    <div className={styles["notifications"]}>
      {copied && (
        <SuccessTag
          target={copyLinkTarget.current}
          show={copied}
          onHide={() => setCopied(false)}
          placement={"top-end"}
          successMsg={"Copied"}
          duration={1000}
        />
      )}
      {error && (
        <ErrorMessage
          show={true}
          onHide={() => setError(undefined)}
          errorMsg={error?.toString()}
        />
      )}
      {success && (
        <SuccessMessage
          show={true}
          onHide={() => setSuccess(undefined)}
          message={"Saved"}
          duration={1000}
        />
      )}
      <div className={styles["notifications__header"]}>
        <h4 className={"rg-32 deep-blue questrial mb-0"}>Notifications</h4>
        <Tabs
          activeKey={tab}
          onSelect={(k) => {
            setTab((s) => k || s);
            navigate(`/pro-dashboard/settings/notifications?tab=${k}`, { replace: true });
          }}
          className={styles["notifications__tabs"]}
        >
          {NOTIFICATION_SETTING_OPTIONS.map((el, i) => (
            <Tab
              key={i}
              eventKey={el.value}
              title={el.label}
              tabClassName={styles["notifications__tab"]}
              onEnter={() =>
                track(AMPLITUDE_PAGE_NAME, "notifications-tab", "click", {
                  eventProperties: { tab: el.value },
                })
              }
            ></Tab>
          ))}
        </Tabs>
      </div>

      <div className={styles["notifications__body"]}>
        {tab === "notifications" && (
          <>
            <div className={styles["notifications__section"]}>
              <div className={styles["notifications__section-title"]}>
                Appointment Notifications
              </div>
              <div className={"rg-14 mb-4"}>
                Get notified about session confirmations, reschedules, and
                cancellations. You will still receive notifications for client
                actions (e.g. booking, rescheduling, canceling).
              </div>

              <div className={styles["notifications__settings-input"]}>
                <ToggleSwitch
                  checked={allow}
                  onChange={onAllowEmailChange}
                  onClick={() =>
                    track(AMPLITUDE_PAGE_NAME, "autopay-toggle", "click")
                  }
                />
                <span aria-hidden="true">Your Appointment Changes</span>
              </div>
            </div>
          </>
        )}
      </div>

      <div className={styles["notifications__footer"]}>
        <div className={"gray01 rg-12"}>
          ©2026 Orchid Exchange Inc. All rights reserved
        </div>
        <Button
          variant="primary"
          onClick={() => {
            track(AMPLITUDE_PAGE_NAME, "save-button", "click");
            saveNotificationsSettings();
          }}
          className={styles["data-analysis__save-button"]}
          disabled={!changed || busy}
        >
          Save{" "}
          {busy && (
            <Spinner
              as="span"
              animation="grow"
              size="sm"
              role="status"
              aria-hidden="true"
              className="ml-3"
            />
          )}
        </Button>
      </div>
    </div>
  );
};

export default NotificationsSetting;
